import { html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { AppComponent } from '@reporting/rp-app-host';
import { Container, dom } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import { AgentSchedule, ETLJob } from '@reporting/rp-types/jobs';
import { map } from 'lit/directives/map.js';
import { Ref, ref, createRef } from 'lit/directives/ref.js';
import { JobWatcherInfo } from './jobWatcherInfo';
import { AgentWatcherInfo } from './agentWatcherInfo';
import { TreeDropdown } from '@omegagrid/tree';
import { JobStatusExt, WatcherItem, WatcherItemType } from '../types';
import { formatSeconds } from '../helpers';
import { Accounting, AccountingRights } from '@reporting/rp-types/core';
import { UpdatesInfo } from './updatesInfo';

const SLEEPING_TRESHOLD = 10000;
const IDLE_TIMEOUT = 60000;
const RUNNING_TIMEOUT = 5000;
const UPDATE_TIMEOUT = 3000;
const PENDING_TIMEOUT = 30; // seconds to wait for a started/stopped job to change its state
const DROPDOWN_MAX_HEIGHT_RATIO = 0.9; // of the window height

const statusIconColorMap: Record<string, string> = {
	'INFO': 'var(--og-colortype-blue-background-color)',
	'WARNING': 'var(--og-colortype-yellow-background-color)',
	'DANGER': 'var(--og-colortype-red-background-color)'
};

@customElement('rp-app-jobwatcher')
export class JobWatcher extends AppComponent {

	static styles = css`
		* {
			box-sizing: border-box;
		}

		:host {
			display: block;
		}

		div[slot="label"] {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: center;
			width: 100%;
		}

		og-dropdown {
			height: 100%;
		}
	`;

	@property({type: Boolean})
	compact = false;

	@state()
	running = false;

	@state()
	items: WatcherItem[];

	@state()
	jobStatuses = new Map<string, JobStatusExt>();

	@state()
	statusIconColor: string|null = null;

	@query('og-dropdown')
	dropdown: TreeDropdown;

	@query('#timer')
	timerElm: HTMLElement;

	_updatesInfoRef = createRef<UpdatesInfo>();
	get updatesInfoComp() { return this._updatesInfoRef.value }

	_dropdownContainerRef = createRef<Container>();
	get dropdownContainer() { return this._dropdownContainerRef.value }

	_dropdownContentRef = createRef<HTMLDivElement>();
	get dropdownContent() { return this._dropdownContentRef.value }

	_jobRefs = new Map<string, Ref<JobWatcherInfo>>();
	_agentRefs = new Map<string, Ref<AgentWatcherInfo>>();
	_firstUpdate = true;
	_timer: number;
	_updateTimeout: number | null = null;
	_paused = false; // the polling loop stopped because the tab got hidden; _onVisibility resumes it
	_startRequests = new Map<string, number>();
	_stopRequests = new Map<string, number>();

	get pendingRequests() { return this._startRequests.size + this._stopRequests.size }
	get agentItems() { return this.items?.filter(item => item.type === WatcherItemType.AGENT) ?? [] }
	/** The agent schedules are polled only while the dropdown is open - nobody sees them otherwise. */
	get watchAgents() { return this.agentItems.length > 0 && !!this.dropdown?.isOpen }
	/** True while a watched agent has a run requested (RUN_NOW) that the customer's agent has not picked up yet. */
	get agentsPending() { return this.watchAgents && this.agentItems.some(item => item.agent.run_now) }
	get jobService() { return this.container.ms('jobs') }
	get etlService() { return this.container.ms('etl') }

	get isSleeping() {
		const tlm = this.container.telemetry;
		return !tlm.pointer.lastMove || (Date.now() - tlm.pointer.lastMove.getTime() > SLEEPING_TRESHOLD);
	}

	getJobStatusElm(name: string) {
		return this._jobRefs.get(name)?.value;
	}

	/** True when the job has been started recently but is not reported as running yet. */
	isStarting(jobName: string) {
		return this._startRequests.has(jobName);
	}

	/** Drops start/stop requests the job has not reacted to, so its state does not stay pending forever. */
	expirePendingRequests() {
		const deadline = (Date.now() / 1000) - PENDING_TIMEOUT;
		this._startRequests.forEach((time, name) => { if (time < deadline) this._startRequests.delete(name) });
		this._stopRequests.forEach((time, name) => { if (time < deadline) this._stopRequests.delete(name) });
	}

	/** Fetches the statuses unless the user is away; returns false when the fetch was skipped. */
	async updateInfos(): Promise<boolean> {
		// pending requests and requested agent runs keep the polling alive, otherwise a job whose state never
		// changes would stay in the starting/stoping state until the user moves the pointer again
		if (this._firstUpdate || !this.isSleeping || this.running || this.pendingRequests > 0 || this.agentsPending) {
			this._firstUpdate = false;
			const jobNames = this.items.filter(item => item.type === WatcherItemType.JOB).map(item => item.job.job_name);
			const [jobStatuses, agentSchedules] = await Promise.all([
				jobNames.length ? this.jobService.get<JobStatusExt[]>('statuses', { jobs: jobNames }) : [],
				this.watchAgents ? this.jobService.get<AgentSchedule[]>('agent/userSchedules') : null,
			]);
			if (agentSchedules) this.applyAgentSchedules(agentSchedules);
			this.running = false;
			this.jobStatuses.clear();
			this.expirePendingRequests();
			jobStatuses.forEach(jobStatus => {
				this.running = jobStatus.running || this.running;
				this.jobStatuses.set(jobStatus.job_name, jobStatus);
				const jobElm = this.getJobStatusElm(jobStatus.job_name);

				if (jobStatus.running) {
					jobStatus._start = (Date.now() / 1000) - jobStatus.execution_time;
					if (jobElm) jobElm.state = 'running';
					this._startRequests.delete(jobStatus.job_name);
				} else if (this.isStarting(jobStatus.job_name)) {
					// a job may start a temporary job doing the actual work - keep the starting
					// state until the temporary job is registered by the sql agent
					if (jobElm) jobElm.state = 'starting';
				} else if (jobElm && jobElm.state !== 'idle') {
					jobElm.state = 'idle';
					this._stopRequests.delete(jobStatus.job_name);
					if (jobElm.job.default_flag) { // refresh updates info only for default jobs
						this.updatesInfoComp?.refresh();
					}
				}
				if (jobElm) jobElm.jobStatus = jobStatus;
			});
			return true;
		}
		return false;
	}

	updateTimer() {
		if (this.running && this._timer == null) {
			this._timer = window.setInterval(() => {
				if (this.dropdown && this.dropdown.isOpen) {
					this._jobRefs.forEach(ref => ref?.value.updateTimer());
				}
				let oldestStart = Infinity;
				this.jobStatuses.forEach(jobStatus => oldestStart = Math.min(oldestStart, jobStatus._start || Infinity));
				this.timerElm.textContent = !this.compact && oldestStart < Infinity && this.timerElm
					? formatSeconds((Date.now() / 1000) - oldestStart)
					: '';
			}, 1000);
		} else if (!this.running && this._timer != null) {
			window.clearInterval(this._timer);
			this._timer = null;
			this.timerElm.textContent = '';
		}
	}

	get timeout() {
		return this.running || this.pendingRequests > 0 || this.agentsPending ? RUNNING_TIMEOUT : IDLE_TIMEOUT;
	}

	_lastUpdate = 0;

	async updateJobs() {
		if (this._updateTimeout != null) { // a single loop at a time (re-connected while the first update was in flight)
			window.clearTimeout(this._updateTimeout);
			this._updateTimeout = null;
		}
		if (!this.isConnected) return;
		if (!this.container.telemetry.visible) { // nobody watches a hidden tab: pause until it is shown again
			this._paused = true;
			return;
		}

		if (Date.now() - this._lastUpdate >= this.timeout) {
			try {
				if (await this.updateInfos()) this._lastUpdate = Date.now();
				this.updateTimer();
			} catch (e) {
				console.error('Job status update failed', e); // a failed update must not kill the polling
				this._lastUpdate = Date.now(); // and a failing server is asked again after the interval, not every tick
			}
		}

		if (this.isConnected) {
			this._updateTimeout = window.setTimeout(() => this.updateJobs(), UPDATE_TIMEOUT);
		}
	}

	_onVisibility = () => {
		if (!this._paused || !this.container?.telemetry.visible) return;
		this._paused = false;
		this._lastUpdate = 0; // the statuses went stale while hidden: refresh right away
		this.updateJobs();
	}

	_onDropdownOpen = () => {
		this.layout();
		if (this.agentItems.length === 0) return;
		this._lastUpdate = 0; // the agents were not polled while closed: refresh right away
		// the loop is waiting for its tick; an update in flight reschedules itself and picks the reset up
		if (this._updateTimeout != null) this.updateJobs();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.container?.telemetry.removeEventListener('visibility', this._onVisibility);
		this._paused = false;
		if (this._updateTimeout != null) {
			window.clearTimeout(this._updateTimeout);
			this._updateTimeout = null;
		}
		if (this._timer != null) { // the elapsed-time ticker; updateTimer() restarts it on the next update
			window.clearInterval(this._timer);
			this._timer = null;
		}
	}

	async connectedCallback() {
		super.connectedCallback();
		this.container.telemetry.addEventListener('visibility', this._onVisibility);
		const isAdmin = this.container.userProvider.hasRight(AccountingRights.Admin);
		const [, ETLJobs, agentSchedules] = await Promise.all([
			this.setStatusIconColor(),
			this.jobService.get<ETLJob[]>('userJobs'),
			// Agent schedules only for admins
			isAdmin
				? this.jobService.get<AgentSchedule[]>('agent/userSchedules')
				: Promise.resolve([] as AgentSchedule[]),
		]);
		this.items = [
			...ETLJobs.map<WatcherItem>(job => ({
				type: WatcherItemType.JOB,
				name: job.job_name,
				ord_num: job.ord_num,
				job,
				agent: null
			})),
			...agentSchedules.map<WatcherItem>(agent => ({
				type: WatcherItemType.AGENT,
				name: agent.name,
				ord_num: agent.ord_num,
				job: null,
				agent
			}))
		];
		this.items.sort((a, b) => a.ord_num - b.ord_num);
		this.updateJobs();
	}

	async setStatusIconColor(forceReload = false) {
		// only refetch when explicitly refreshing (rp-app-updatesinfo @refresh).
		const a = forceReload || !this.container.accountingProvider.accounting
			? await this.container.ms('accounting').get<Accounting>('')
			: this.container.accountingProvider.accounting;
		const priorityOrder = ['DANGER', 'WARNING', 'INFO'] as const;

		const types: string[] = [];
		if (a.fin_flag && this.container.userProvider.hasRight(AccountingRights.Finance) && a.fin_remark) {
			types.push(a.fin_remark_type);
		}
		if (a.item_flag && this.container.userProvider.hasRight(AccountingRights.Items) && a.item_remark) {
			types.push(a.item_remark_type);
		}
		if (a.balance_flag && this.container.userProvider.hasRight(AccountingRights.Aging) && a.balance_remark) {
			types.push(a.balance_remark_type);
		}

		const highestPriority = priorityOrder.find(priority => types.includes(priority));
		this.statusIconColor = highestPriority ? statusIconColorMap[highestPriority] : null;
	}

	startJob(job: ETLJob) {
		const jobElm = this.getJobStatusElm(job.job_name);
		const params = jobElm?.values || {};
		params.job_name = job.job_name;
		this._startRequests.set(job.job_name, Date.now() / 1000);
		if (jobElm) jobElm.state = 'starting';
		this.jobService.post('start', params);
	}

	stopJob(job: ETLJob) {
		this._stopRequests.set(job.job_name, Date.now() / 1000);
		if (this._jobRefs.has(job.job_name)) this._jobRefs.get(job.job_name).value.state = 'stoping';
		this.jobService.post('stop', { job_name: job.job_name });
	}

	startAgent(item: WatcherItem) {
		return this.updateAgent(item, true);
	}

	stopAgent(item: WatcherItem) {
		return this.updateAgent(item, false);
	}

	/** Saves the schedule with the new RUN_NOW; the customer's agent picks a requested run up on its next check. */
	async updateAgent(item: WatcherItem, runNow: boolean) {
		const agentElm = this._agentRefs.get(item.name)?.value;
		if (agentElm) agentElm.pending = true;
		try {
			const schedule = await this.jobService.post<AgentSchedule>('agent/schedules', { ...item.agent, run_now: runNow });
			this.setAgentSchedule(item, schedule);
		} finally {
			if (agentElm) agentElm.pending = false;
		}
	}

	/**
	 * Takes the polled schedules over: the customer's agent resets RUN_NOW and moves NEXT_RUN when it runs,
	 * and the row gets a new IDZ with every change.
	 */
	applyAgentSchedules(schedules: AgentSchedule[]) {
		const byId = new Map(schedules.map(schedule => [schedule.id_agent_schedule, schedule]));
		this.agentItems.forEach(item => {
			const schedule = byId.get(item.agent.id_agent_schedule);
			if (schedule) this.setAgentSchedule(item, schedule);
		});
	}

	/** The item keeps the current row for the next start/stop, the info component shows it. */
	setAgentSchedule(item: WatcherItem, schedule: AgentSchedule) {
		item.agent = schedule;
		const agentElm = this._agentRefs.get(item.name)?.value;
		if (agentElm) agentElm.schedule = schedule;
	}

	layout() {
		if (this.dropdownContainer && this.dropdownContent) {
			dom.setSize(this.dropdownContainer, {
				h: Math.min(this.dropdownContent.offsetHeight, Math.round(window.innerHeight * DROPDOWN_MAX_HEIGHT_RATIO))
			});
			this.dropdown?.layout();
		}
	}

	updated() {
		this.layout();
	}

	render = () => html`
		<og-dropdown
			color="transparent"
			@dropdown.open="${this._onDropdownOpen}"
			nocaret>
			<div slot="label" style="white-space: nowrap">
				${this.running ? html`
					<og-loader icon="refresh"></og-loader>
				` : html`
					<og-icon .icon="${ {
						icon: 'refresh',
						statusIcon: this.statusIconColor ? ['fas', 'circle'] : null,
						statusColor: this.statusIconColor,
					}}"></og-icon>
				`}
				<span style="margin-left: 4px;" id="timer"></span>
			</div>
			<div slot="content" style="overflow: hidden; position: relative; max-width: 350px">
				<og-container ${ref(this._dropdownContainerRef)}>
					<div slot="content" ${ref(this._dropdownContentRef)} style="margin-right: 11px;">
						<rp-app-updatesinfo
							${ref(this._updatesInfoRef)}
							@refresh="${() => this.setStatusIconColor(true)}"
							@resizestop="${() => this.layout()}">
						</rp-app-updatesinfo>
						${this.items?.length ? map(this.items, item => {
							const name = item.name;
							return item.type === WatcherItemType.JOB ? html`
								<rp-app-jobwatcherinfo
									${ref(this._jobRefs.get(name) ?? this._jobRefs.set(name, createRef()).get(name))}
									.etlService="${this.etlService}"
									.jobService="${this.jobService}"
									.job="${item.job}"
									.jobStatus="${this.jobStatuses.get(item.job.job_name)}"
									@start="${() => this.startJob(item.job)}"
									@stop="${() => this.stopJob(item.job)}"
									@resizestop="${() => this.layout()}">
								</rp-app-jobwatcherinfo>
							` : html`
								<rp-app-agentwatcherinfo
									${ref(this._agentRefs.get(name) ?? this._agentRefs.set(name, createRef()).get(name))}
									.schedule="${item.agent}"
									@start="${() => this.startAgent(item)}"
									@stop="${() => this.stopAgent(item)}"
									@resizestop="${() => this.layout()}">
								</rp-app-agentwatcherinfo>
							`;
						}) : html`<div style="padding: 4px;">${msg('No jobs available.')}</div>`}
					</div>
				</og-container>
			</div>
		</og-dropdown>
	`;

}
